import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Navigation from './Navigation';
import PropTypes from 'prop-types'; 
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import { getOrders, getPrintfulOrder } from '../services/api';
import toast from 'react-hot-toast';
import logger from '../utils/logger';

const OrderHistory = ({ cartItems, handleUpdateQuantity, handleRemoveItem }) => {
  const [orders, setOrders] = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(true);
  const [retryCount, setRetryCount] = useState(0);
  const [printfulStatuses, setPrintfulStatuses] = useState({}); // Store Printful order statuses
  const [loadingPrintfulStatuses, setLoadingPrintfulStatuses] = useState(new Set()); // Track which orders are being fetched
  const navigate = useNavigate();
  const { user, loading: authLoading, refreshAuth } = useAuth();
  const isAuthenticated = !!user;

  // Function to fetch Printful order status for a specific order
  const fetchPrintfulOrderStatus = async (order) => {
    if (!order.printfulOrderId) return; // Skip if no Printful order ID
    
    const orderId = order._id;
    
    // Avoid duplicate requests
    if (loadingPrintfulStatuses.has(orderId)) return;
    
    setLoadingPrintfulStatuses(prev => new Set(prev).add(orderId));
    
    try {
      logger.debug('Fetching Printful order status', {
        localOrderId: orderId,
        printfulOrderId: order.printfulOrderId
      });
      
      const printfulOrder = await getPrintfulOrder(order.printfulOrderId);
      
      setPrintfulStatuses(prev => ({
        ...prev,
        [orderId]: {
          status: printfulOrder.status,
          tracking: printfulOrder.tracking_number,
          trackingUrl: printfulOrder.tracking_url,
          items: printfulOrder.items || [],
          updatedAt: new Date().toISOString()
        }
      }));
      
      logger.debug('Successfully fetched Printful order status', {
        localOrderId: orderId,
        printfulStatus: printfulOrder.status
      });
      
    } catch (error) {
      logger.warn('Failed to fetch Printful order status', {
        localOrderId: orderId,
        printfulOrderId: order.printfulOrderId,
        error: error.message
      });
      
      // Store error state to avoid repeated failed requests
      setPrintfulStatuses(prev => ({
        ...prev,
        [orderId]: {
          status: 'unknown',
          error: error.message,
          updatedAt: new Date().toISOString()
        }
      }));
    } finally {
      setLoadingPrintfulStatuses(prev => {
        const newSet = new Set(prev);
        newSet.delete(orderId);
        return newSet;
      });
    }
  };

  // Function to fetch Printful statuses for all orders with Printful order IDs
  const fetchAllPrintfulStatuses = async (ordersList) => {
    const printfulOrders = ordersList.filter(order => order.printfulOrderId);
    
    if (printfulOrders.length === 0) return;
    
    logger.debug(`Fetching Printful statuses for ${printfulOrders.length} orders`);
    
    // Fetch statuses in parallel but limit concurrency to avoid overwhelming the API
    const batchSize = 3;
    for (let i = 0; i < printfulOrders.length; i += batchSize) {
      const batch = printfulOrders.slice(i, i + batchSize);
      await Promise.allSettled(batch.map(fetchPrintfulOrderStatus));
    }
  };

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        // Check if auth context is still loading
        if (authLoading) {
          logger.debug('Auth context still loading, delaying order fetch');
          return;
        }
        
        if (!isAuthenticated) {
          logger.debug('User not authenticated, redirecting to login');
          navigate('/login');
          return;
        }

        logger.component('OrderHistory', 'fetching orders', { userId: user?._id });
        const data = await getOrders();
        logger.debug('Order history fetched successfully', { 
          count: data.length,
          userId: user?._id 
        });
        
        setOrders(data);
        // Reset retry count on successful fetch
        setRetryCount(0);
        
        // Fetch Printful order statuses for orders that have Printful order IDs
        await fetchAllPrintfulStatuses(data);
      } catch (error) {
        logger.error('Failed to fetch order history', { 
          error: error.message,
          userId: user?._id,
          retryCount 
        });
        
        // If the error is due to authentication, try refreshing auth state
        if (error.message === "Not authenticated") {
          logger.auth('Authentication error while fetching orders, attempting refresh');
          
          // Only try to refresh a limited number of times to avoid infinite loops
          if (retryCount < 2) {
            logger.debug(`Retry attempt ${retryCount + 1} for order history fetch`);
            refreshAuth();
            setRetryCount(prev => prev + 1);
            return; // Don't set ordersLoading to false yet
          } else {
            logger.auth('Max retries reached, redirecting to login');
            toast.error('Authentication issue. Please try logging in again.');
            navigate('/login');
          }
        } else {
          toast.error('Could not retrieve your order history');
        }
      } finally {
        setOrdersLoading(false);
      }
    };

    fetchOrders();
  }, [isAuthenticated, navigate, authLoading, refreshAuth, retryCount, user]);

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  // Helper function to get enhanced order status
  const getOrderStatus = (order) => {
    const printfulStatus = printfulStatuses[order._id];
    const isLoadingPrintful = loadingPrintfulStatuses.has(order._id);
    
    // If we have a Printful order ID but no status yet, show loading
    if (order.printfulOrderId && isLoadingPrintful) {
      return {
        text: 'Loading status...',
        color: 'text-blue-500 dark:text-blue-400',
        showTracking: false
      };
    }
    
    // If we have Printful status, use it
    if (printfulStatus && !printfulStatus.error) {
      const status = printfulStatus.status;
      let statusText = status;
      let statusColor = 'text-gray-500 dark:text-gray-400';
      
      switch (status) {
        case 'draft':
          statusText = 'Order Received';
          statusColor = 'text-blue-500 dark:text-blue-400';
          break;
        case 'pending':
          statusText = 'Being Processed';
          statusColor = 'text-yellow-500 dark:text-yellow-400';
          break;
        case 'fulfilled':
          statusText = 'Fulfilled';
          statusColor = 'text-green-500 dark:text-green-400';
          break;
        case 'shipped':
          statusText = 'Shipped';
          statusColor = 'text-green-600 dark:text-green-500';
          break;
        case 'canceled':
          statusText = 'Canceled';
          statusColor = 'text-red-500 dark:text-red-400';
          break;
        default:
          statusText = status.charAt(0).toUpperCase() + status.slice(1);
      }
      
      return {
        text: statusText,
        color: statusColor,
        showTracking: printfulStatus.tracking && (status === 'shipped' || status === 'fulfilled'),
        tracking: printfulStatus.tracking,
        trackingUrl: printfulStatus.trackingUrl
      };
    }
    
    // Fallback to basic status
    return {
      text: order.isPaid ? 'Paid' : 'Processing',
      color: order.isPaid ? 'text-green-500 dark:text-green-400' : 'text-yellow-500 dark:text-yellow-400',
      showTracking: false
    };
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <Navigation
        cartItems={cartItems}
        handleUpdateQuantity={handleUpdateQuantity}
        handleRemoveItem={handleRemoveItem}
      />
      <div className="container mx-auto px-4 py-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-6 font-handwritten">
          Order History
        </h1>

        {ordersLoading ? (
          <div className="text-center py-8">
            <p className="text-gray-600 dark:text-gray-300">Loading your orders...</p>
          </div>
        ) : orders.length === 0 ? (
          <div className="text-center py-8 bg-white dark:bg-gray-800 rounded-lg shadow-md p-6">
            <p className="text-gray-600 dark:text-gray-300 mb-4">
              You haven&apos;t placed any orders yet.
            </p>
            <Button
              onClick={() => navigate('/shop')}
              className="bg-blue-600 hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600 text-white font-handwritten"
            >
              Start Shopping
            </Button>
          </div>
        ) : (
          <div className="space-y-6">
            {orders.map((order) => (
              <div
                key={order._id}
                className="bg-white dark:bg-gray-800 rounded-lg shadow-md overflow-hidden"
              >
                <div className="p-4 bg-gray-100 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-600">
                  <div className="flex flex-wrap justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Order Placed: {formatDate(order.createdAt)}
                      </p>
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Order ID: {order._id}
                      </p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500 dark:text-gray-400">
                        Total: ${order.totalPrice.toFixed(2)}
                      </p>
                      {(() => {
                        const status = getOrderStatus(order);
                        return (
                          <div>
                            <p className={`text-sm ${status.color}`}>
                              Status: {status.text}
                            </p>
                            {status.showTracking && (
                              <p className="text-xs text-blue-500 dark:text-blue-400 mt-1">
                                {status.trackingUrl ? (
                                  <a 
                                    href={status.trackingUrl} 
                                    target="_blank" 
                                    rel="noopener noreferrer"
                                    className="hover:underline"
                                  >
                                    Track: {status.tracking}
                                  </a>
                                ) : (
                                  `Tracking: ${status.tracking}`
                                )}
                              </p>
                            )}
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>

                <div className="p-4">
                  <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                    Items
                  </h3>
                  <div className="space-y-3">
                    {order.orderItems.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-start justify-between py-2 border-b border-gray-200 dark:border-gray-700 last:border-0"
                      >
                        <div className="flex items-start space-x-4">
                          {item.image && (
                            <div className="w-16 h-16 bg-gray-200 dark:bg-gray-700 rounded overflow-hidden">
                              <img
                                src={item.image}
                                alt={item.name}
                                className="w-full h-full object-cover"
                              />
                            </div>
                          )}
                          <div>
                            <p className="text-gray-900 dark:text-white font-medium">
                              {item.name}
                            </p>
                            <p className="text-sm text-gray-500 dark:text-gray-400">
                              Quantity: {item.quantity}
                            </p>
                            {item.size && (
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                Size: {item.size}
                              </p>
                            )}
                            {item.color && (
                              <p className="text-sm text-gray-500 dark:text-gray-400">
                                Color: {item.color}
                              </p>
                            )}
                          </div>
                        </div>
                        <p className="text-gray-900 dark:text-white">
                          ${(item.price * item.quantity).toFixed(2)}
                        </p>
                      </div>
                    ))}
                  </div>

                  {order.shippingAddress && (
                    <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                      <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                        Shipping Address
                      </h3>
                      <p className="text-gray-700 dark:text-gray-300">
                        {order.shippingAddress.street}<br />
                        {order.shippingAddress.street2 && (
                          <>{order.shippingAddress.street2}<br /></>
                        )}
                        {order.shippingAddress.city}, {order.shippingAddress.state} {order.shippingAddress.zipCode}<br />
                        {order.shippingAddress.country}
                      </p>
                    </div>
                  )}

                  <div className="mt-4 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <div className="flex justify-between">
                      <p className="text-gray-700 dark:text-gray-300">Subtotal:</p>
                      <p className="text-gray-900 dark:text-white">
                        ${order.itemsPrice.toFixed(2)}
                      </p>
                    </div>
                    <div className="flex justify-between mt-1">
                      <p className="text-gray-700 dark:text-gray-300">Tax:</p>
                      <p className="text-gray-900 dark:text-white">
                        ${order.taxPrice.toFixed(2)}
                      </p>
                    </div>
                    <div className="flex justify-between mt-1">
                      <p className="text-gray-700 dark:text-gray-300">Shipping:</p>
                      <p className="text-gray-900 dark:text-white">
                        ${order.shippingPrice.toFixed(2)}
                      </p>
                    </div>
                    <div className="flex justify-between mt-2 pt-2 border-t border-gray-200 dark:border-gray-700">
                      <p className="font-semibold text-gray-900 dark:text-white">Total:</p>
                      <p className="font-semibold text-blue-600 dark:text-blue-400">
                        ${order.totalPrice.toFixed(2)}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

OrderHistory.propTypes = {
  cartItems: PropTypes.arrayOf(PropTypes.object).isRequired,
  handleUpdateQuantity: PropTypes.func.isRequired,
  handleRemoveItem: PropTypes.func.isRequired,
};

export default OrderHistory;
